import React, { useCallback, useState } from "react";
import { useSelector } from "react-redux";

import { ProductBundleModel } from "@api/mappers/productBundles.mapper";
import { ProductTileModel } from "@api/mappers/products.mapper";

import { getPossibleDeliveryOptions } from "@utils/deliveryMethod";
import { DeliveryTypes, StorePickerModalStates } from "@utils/enums";
import { isDefined } from "@utils/helpers";
import { getPreferredStoreCodeForStock } from "@utils/store";

import { useThunkDispatch } from "@redux/dispatcher";
import { addToCart } from "@redux/modules/api/data/cart/cart.module";
import { getCart } from "@redux/modules/api/data/cart/cart.selectors";
import { StoreDetailsModel } from "@redux/modules/api/data/stores/@types/storeDetails.model";
import { getDeliveryType } from "@redux/modules/value/productDetail/productDetail.module";
import { useGranite } from "@redux/modules/value/translations/translations.provider";

import { ProductBundleComponent } from "@components/Product/ProductBundles/ProductBundle/productBundle.component";
import { StorePickerModal } from "@components/Store/StorePicker/storePickerModal.container";
import { StorePickerFeature } from "@components/Store/StorePicker/types/storePickerFeature.enum";
import { StorePickerType } from "@components/Store/StorePicker/types/storePickerType.enum";

type ProductBundleProps = {
    mainProduct: ProductTileModel;
    productBundle: ProductBundleModel;
};

type AddToCartEntry = {
    product: ProductTileModel;
    quantity: number;
    shopId?: string;
    deliveryType: DeliveryTypes;
};

const isDSVOnly = (product: ProductTileModel) =>
    product.deliveryTypes.length === 1 &&
    product.deliveryTypes[0] === DeliveryTypes.HomeDelivery;

const getDefaultAddToCartOptions = (product: ProductTileModel) => ({
    deliveryType: DeliveryTypes.HomeDelivery,
    quantity: product.minimumOrderQuantity ?? 1,
});

const getDeliveryOptions = (product: ProductTileModel) => {
    if (isDSVOnly(product)) {
        return getDefaultAddToCartOptions(product);
    } else {
        const deliveryOptions = getPossibleDeliveryOptions(
            product.productVisibilityType,
            product.deliveryTypes || [],
        );
        const preferredDeliveryType = getDeliveryType();

        const deliveryType = deliveryOptions.includes(preferredDeliveryType)
            ? preferredDeliveryType
            : deliveryOptions[0];

        return deliveryType === DeliveryTypes.ClickAndCollect
            ? {
                  deliveryType: deliveryType,
                  quantity: 1,
                  shopId: getPreferredStoreCodeForStock(),
              }
            : {
                  deliveryType: deliveryType,
                  quantity: product.minimumOrderQuantity,
              };
    }
};

const getAddToCartEntries =
    (products: Array<ProductTileModel>) =>
    (preferredShopId: string | undefined): Array<AddToCartEntry> =>
        products.map(product => {
            const options = getDeliveryOptions(product);

            return {
                product: product,
                deliveryType: options.deliveryType,
                quantity: options.quantity,
                shopId:
                    options.deliveryType === DeliveryTypes.ClickAndCollect
                        ? preferredShopId
                        : undefined,
            };
        });

const isInvalidAddToCartEntry = ({ deliveryType, shopId }: AddToCartEntry) =>
    deliveryType === DeliveryTypes.ClickAndCollect && !isDefined(shopId);

const ProductBundle = ({ mainProduct, productBundle }: ProductBundleProps) => {
    const { t } = useGranite();

    const dispatch = useThunkDispatch();
    const cart = useSelector(getCart);

    const [storePickerModalState, setStorePickerModalState] =
        useState<StorePickerModalStates>(StorePickerModalStates.Closed);
    const [addToCartLoading, setAddToCartLoading] = useState<boolean>(false);

    const getPreferredEntriesByPreferredShopId = useCallback(
        getAddToCartEntries([mainProduct, ...productBundle.bundleProducts]),
        [mainProduct, productBundle.bundleProducts],
    );

    const addEntryToCart = async (entry: AddToCartEntry) => {
        const productInCart = cart?.items.find(
            cartItem => cartItem.sku === entry.product.sku,
        );
        if (productInCart) return;

        await dispatch(
            addToCart(
                {
                    shopId: entry.shopId,
                    sku: entry.product.sku,
                    quantity: entry.quantity,
                    deliveryType: entry.deliveryType,
                },
                entry.product,
            ),
        );
    };

    const addBundleToCart = async () => {
        setAddToCartLoading(true);
        for await (const entry of getPreferredEntriesByPreferredShopId(
            getPreferredStoreCodeForStock(),
        )) {
            await addEntryToCart(entry);
        }
        setAddToCartLoading(false);
    };

    const onAddToCart = async () => {
        // Show storePickerModal if bundle has product with C&C only and no store selected
        getPreferredEntriesByPreferredShopId(
            getPreferredStoreCodeForStock(),
        ).some(isInvalidAddToCartEntry)
            ? setStorePickerModalState(StorePickerModalStates.AddToCart)
            : addBundleToCart();
    };

    const handleOnStorePickerModalClose = () =>
        setStorePickerModalState(StorePickerModalStates.Closed);

    const handleOnStorePickerModalSubmit = async (
        _store: StoreDetailsModel,
        onClose?: () => void,
    ) => {
        addBundleToCart();
        onClose && onClose();
    };

    return (
        <>
            <ProductBundleComponent
                cart={cart ?? undefined}
                mainProduct={mainProduct}
                bundleProducts={productBundle.bundleProducts}
                onAddToCart={onAddToCart}
                addToCartLoading={addToCartLoading}
            />
            <StorePickerModal
                type={StorePickerType.SELECT}
                isOpen={storePickerModalState !== StorePickerModalStates.Closed}
                storeCode={getPreferredStoreCodeForStock() ?? null}
                submitButtonText={t(
                    "general.storepicker.button.confirmStoreAndOrder",
                )}
                infoLink={null}
                fixedFeatures={[StorePickerFeature.BUY_ONLINE_PICKUP_IN_STORE]}
                displayStockLevel={false}
                onSubmit={handleOnStorePickerModalSubmit}
                onClose={handleOnStorePickerModalClose}
            />
        </>
    );
};

export { ProductBundle };
